import * as React from "react";
import {
  Button,
  Space,
  Tabs,
  Typography,
  Tooltip as TooltipBase,
  notification,
  Spin,
} from "antd";
import { Link, RouteComponentProps } from "react-router-dom";
import { Badge } from "antd";
import { TReducers } from "@features/reducers";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import {
  setAccessingFundsAgreementCreatedAt,
  setFundsData,
} from "@funds/features/action.funds";
import { connect } from "react-redux";
import FundsTab from "./tabs/FundsTab";
import BankAccountTab from "./tabs/BankAccountTab";
import WalletsTab from "./tabs/WalletsTab";
import TransactionHistoryTab from "./tabs/TransactionHistoryTab";
import StakingTab from "./tabs/StakingTab";
import FundsSVG from "@images/funds/funds.svg";
import BankAccountSVG from "@images/funds/bank-account.svg";
import StakingSVG from "@images/funds/staking.svg";
import TransactionSVG from "@images/funds/transaction.svg";
import WalletsSVG from "@images/funds/wallets.svg";
import { classNames } from "@features/utils/reactFunctions";
import {
  FUNDS_DEPOSIT_FORM,
  FUNDS_DEPOSIT_WITH_BANK_TRANSFER_URL,
} from "@funds/config";
import { FundsAppContext } from "@features/context";
import { isTier1AndAbove } from "@funds/features/helpers/functions";
import InvestmentRiskModal from "@funds/components/InvestmentRiskModal/InvestmentRiskModal";
import { TUserAgreements } from "@features/reducer.info";
import { RiskModalProps } from "./utils";
import { AGREEMENT_TYPES } from "@services/UserAgreements.service";

import * as style from "./MyFundsStyles.module.scss";
import * as fundsStyle from "../../FundsAppStyle.module.scss";

const stakingEthBlocked = (reason: string) => ({
  label: (
    <TooltipBase title={reason}>
      <div className={style.tabWrapper}>
        <StakingSVG /> <span>Staking ETH</span>
      </div>
    </TooltipBase>
  ),
  key: "stakingEth",
  disabled: true,
  children: <></>,
});

const stakingEth = {
  label: (
    <Link to="stakingEth">
      <div className={style.tabWrapper}>
        <StakingSVG /> <span>Staking ETH</span>
      </div>
    </Link>
  ),
  key: "stakingEth",
  children: <StakingTab />,
};

const myFundsTab = (setRiskModalProps: (props: RiskModalProps) => void) => ({
  label: (
    <Link to="myFunds">
      <div className={style.tabWrapper}>
        <FundsSVG /> <span>Funds</span>
      </div>
    </Link>
  ),
  key: "myFunds",
  children: <FundsTab setRiskModalProps={setRiskModalProps} />,
});

const baseTabs = [
  {
    label: (
      <Link to="bank">
        <div className={style.tabWrapper}>
          <BankAccountSVG /> <span>Bank Account</span>
        </div>
      </Link>
    ),
    key: "bank",
    children: <BankAccountTab />,
  },
  {
    label: (
      <Link to="wallets">
        <div className={style.tabWrapper}>
          <WalletsSVG /> <span>Wallets</span>
        </div>
      </Link>
    ),
    key: "wallets",
    children: <WalletsTab />,
  },
  {
    label: (
      <Link to="transactions">
        <div className={style.tabWrapper}>
          <TransactionSVG /> <span>Transaction history</span>
        </div>
      </Link>
    ),
    key: "transactions",
    children: <TransactionHistoryTab />,
  },
];

type Props = {
  setFundsData: (investorId: number) => any;
  companyInvestorId: number | undefined;
  investorId: number | undefined;
  location: RouteComponentProps<{}>["location"];
  match: RouteComponentProps<{ subpage: string }>["match"];
  history: RouteComponentProps<{}>["history"];
  tier: string;
  us_resident: boolean;
  agreements: TUserAgreements;
  country: string;
};

export const tier1RequiredMessage =
  "You need to be at least tier 1 to access this feature";
export const tier1UkRestrictedMessage =
  "To enable deposits, please complete the additional UK regulation information required. Visit your BFID dashboard to finalise the pending session.";
export const productRestrictedForUsMessage =
  "This product is not available for US persons";

const MyFunds = ({
  setFundsData,
  companyInvestorId,
  investorId,
  match,
  history,
  tier,
  us_resident,
  country,
}: Props) => {
  const { appLink } = React.useContext(FundsAppContext);
  const [activeTab, setActiveTab] = React.useState("funds");
  const [loading, isLoading] = React.useState(true);
  const [riskModalProps, setRiskModalProps] = React.useState<RiskModalProps>();

  const [tabs, setTabs] = React.useState([
    myFundsTab(setRiskModalProps),
    stakingEth,
    ...baseTabs,
  ]);

  const isUK = React.useMemo(() => country === "GB", [country]);

  React.useEffect(() => {
    if (us_resident) {
      setTabs([
        myFundsTab(setRiskModalProps),
        stakingEthBlocked(productRestrictedForUsMessage),
        ...baseTabs,
      ]);
      return;
    }

    if (isTier1AndAbove(tier)) {
      setTabs([myFundsTab(setRiskModalProps), stakingEth, ...baseTabs]);
      return;
    }

    setTabs([myFundsTab(setRiskModalProps), stakingEth, ...baseTabs]);
  }, [loading]);

  React.useEffect(() => {
    if (
      !match.params.subpage ||
      tabs.filter((t) => t.key === match.params.subpage).length === 0
    )
      history.push(match.url + "/" + tabs[0].key);

    setActiveTab(match.params.subpage);
  }, [match.params.subpage]);

  React.useEffect(() => {
    const id = companyInvestorId || investorId;

    if (!id) throw new Error("ID is missing");

    setFundsData(id)
      .then(() => {
        isLoading(false);
      })
      .catch(() => {
        isLoading(false);
        notification.error({ message: "Something went wrong" });
      });
  }, []);

  return (
    <>
      <div className={style.fundsPage}>
        <Space align="center" className={style.fundsPageHeader}>
          <Space direction="vertical" className={style.headerTitleWrapper}>
            <Typography.Title level={2}>My Funds</Typography.Title>
            <Tooltip
              title="Custody of funds"
              showArrow={false}
              tooltipMessage="Client funds are held in the custody of the Virtual Assets Broker situated in the British Virgin Islands. The BF Platform Operator, based in the Cayman Islands, does not provide custody services for clients' funds, nor does it engage in any virtual asset services."
            />
          </Space>
          <Space className={style.headerButtonsWrapper}>
            <div className={classNames(fundsStyle.button)}>
              <TooltipBase title="Coming soon">
                <Button disabled>Auto-Invest</Button>
              </TooltipBase>
            </div>
            <Link
              className={classNames(fundsStyle.button, fundsStyle.buttonGreen)}
              to={`${appLink}${FUNDS_DEPOSIT_WITH_BANK_TRANSFER_URL}`}
            >
              <Button style={{ padding: "10px 20px" }}>
                Deposit with bank transfer
              </Button>
            </Link>
          </Space>
        </Space>

        {loading ? (
          <Space
            align="center"
            style={{ justifyContent: "center", width: "100%", minHeight: 300 }}
          >
            <Spin size="large" />
          </Space>
        ) : (
          <Tabs
            defaultActiveKey={tabs[0].key}
            activeKey={activeTab}
            items={tabs}
            destroyInactiveTabPane
          />
        )}
      </div>

      <InvestmentRiskModal
        history={history}
        isOpen={!!riskModalProps}
        onClose={() => setRiskModalProps(undefined)}
        {...riskModalProps}
      />
    </>
  );
};

const mapStateToProps = (store: TReducers) => ({
  investorId: store.userInfo.investor_id,
  companyInvestorId: store.companyReducer.companyDetails.investor_id,
  tier: store.userInfo.tier,
  us_resident: store.userInfo.us_resident,
  agreements: store.userInfo.agreements,
  country: store.userInfo.country,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setFundsData: (investorId: number) => dispatch(setFundsData(investorId)),
  setAccessingFundsAgreementCreatedAt: () =>
    dispatch(setAccessingFundsAgreementCreatedAt()),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyFunds);
